import { createSelector } from '@reduxjs/toolkit';
import { edgeApi } from '../services/edgeApi';
import { RootState } from '@/store';

// https://redux.js.org/tutorials/essentials/part-8-rtk-query-advanced#selecting-users-data
export const selectMapPointsResult = edgeApi.endpoints.getMapPoints.select();
export const selectMapNodesResult =
  edgeApi.endpoints.getMapNodes.select(undefined);
export const selectMapEdgesResult = edgeApi.endpoints.getMapEdges.select();
export const selectTspsResult = edgeApi.endpoints.getTSPs.select(undefined);
export const selectInterconnectsResult =
  edgeApi.endpoints.getInterconnects.select(undefined);
export const selectPriceComponentsResult =
  edgeApi.endpoints.getPriceComponents.select({});

export const selectStartNode = (state: RootState) =>
  state.plotter.activeEdge?.start_node
    ? state.plotter.nodes[state.plotter.activeEdge?.start_node]
    : null;

export const selectEndNode = (state: RootState) =>
  state.plotter.activeEdge?.end_node
    ? state.plotter.nodes[state.plotter.activeEdge?.end_node]
    : null;

export const selectRiseChains = (state: RootState) =>
  state.plotter.riseChains ?? [];

export const selectRiseChainsLoading = (state: RootState) =>
  state.plotter.riseChainsLoading;

export const selectAllRiseChains = createSelector(
  selectRiseChains,
  (transports) => transports,
);

export const selectShowRiseChains = (state: RootState) =>
  state.plotter.showRiseChains;

export const selectPlotterCapacities = createSelector(
  (state: RootState) => state?.plotter?.capacities ?? [],
  (capacities) => capacities ?? [],
);

export const selectPlotterMarkets = createSelector(
  (state: RootState) => state.plotter.markets,
  (markets) => markets ?? [],
);

export const selectNodeSize = createSelector(
  (state: RootState) => state.plotter.nodeSize,
  (nodeSize) => Number(nodeSize),
);

export const selectPathMode = createSelector(
  (state: RootState) => state.plotter.pathMode,
  (pathMode) => pathMode,
);

export const selectShowEdgeDirections = createSelector(
  (state: RootState) => state.plotter.showEdgeDirections,
  (showEdgeDirections) => showEdgeDirections,
);

export const selectFocusedNode = createSelector(
  (state: RootState) => state.plotter.focusedNode,
  (focusedNode) => focusedNode,
);

export const selectMapView = createSelector(
  (state: RootState) => state.plotter.mapView,
  (mapView) => mapView,
);

export const selectMapFit = createSelector(
  (state: RootState) => state.plotter.mapFit,
  (mapFit) => mapFit,
);

export const selectShowFlowArrows = createSelector(
  (state: RootState) => state.plotter.showFlowArrows,
  (showFlowArrows) => showFlowArrows,
);

export const selectShowNoticeMarkers = createSelector(
  (state: RootState) => state.plotter.showNoticeMarkers,
  (showNoticeMarkers) => showNoticeMarkers,
);

export const selectShowConstraints = createSelector(
  (state: RootState) => state.plotter.showConstraints,
  (showConstraints) => showConstraints,
);

export const selectShowSegmentCapacityComponents = createSelector(
  (state: RootState) => state.plotter.showSegmentCapacityComponents,
  (showSegmentCapacityComponents) => showSegmentCapacityComponents,
);

export const selectShowPointCapacityComponents = createSelector(
  (state: RootState) => state.plotter.showPointCapacityComponents,
  (showPointCapacityComponents) => showPointCapacityComponents,
);

export const selectShowPriceComponents = createSelector(
  (state: RootState) => state.plotter.showPriceComponents,
  (showPriceComponents) => showPriceComponents,
);

export const selectShowNoticeComponents = createSelector(
  (state: RootState) => state.plotter.showNoticeComponents,
  (showNoticeComponents) => showNoticeComponents,
);

export const selectLayers = createSelector(
  (state: RootState) => state.plotter.layers,
  (layers) => layers,
);

export const selectLabelDisplayOptions = createSelector(
  (state: RootState) => state.plotter.labels,
  (labels) => labels,
);
